import {
	ClickAwayListener,
	Grow,
	MenuList,
	Paper,
	Popper,
	PopperProps,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { palette, riskPalette } from '@innovyze/stylovyze';

import { StyledMenuItem, Wrapper } from './ScoreButton.styles';
import RoundButton from '../RoundButton/RoundButton.component';

export interface Option {
	icon?: React.ReactNode;
	name: string;
	onClick?: () => void;
	cy?: string;
	// internal use - the original index before removing item
	key?: number;
}

const addKey = (option: Option, i: number): Option => ({
	...option,
	key: i,
});

type Variant = 'contained' | 'outlined' | 'text';
export interface ScoreButtonProps {
	/**
	 * Menu Options, includes : { icon?: ReactNode, name: string, onClick?: function,
	 * cy?: string }
	 */
	options: Option[];
	/**
	 * MUI Button Base Variant
	 */
	variant?: Variant;
	/**
	 * Passthrough for classname
	 */
	className?: string;
	/**
	 * Whether the down arrow is used on the right
	 */
	arrow?: boolean;
	/**
	 * Whether the selected button text switches or remains your first item
	 */
	switchSelected?: boolean;
	/**
	 * MUI button color - needs to use our theme's palette
	 */
	color?: 'primary' | 'secondary';
	/**
	 * Where the popper is anchored to on our button
	 */
	placement?: PopperProps['placement'];
	/**
	 * Initial selected index
	 */
	initialSelectedIndex?: number;

	/**
	 * Fit the width of the menu to it's contents, minimum width will be the width specified
	 */
	fitMenuToContent?: boolean;

	onSelectChanged?: (selectedValue: string) => void;
}

/**
 * Add a description for your component here
 */
export const ScoreButton = ({
	options,
	switchSelected,
	placement,
	initialSelectedIndex,
	onSelectChanged,
}: ScoreButtonProps) => {
	const [selectedIndex, setSelectedIndex] = useState(
		initialSelectedIndex ? initialSelectedIndex : 0,
	);
	useEffect(() => {
		setSelectedIndex(initialSelectedIndex ? initialSelectedIndex : 0);
	}, [initialSelectedIndex]);
	const anchorRef = useRef<HTMLDivElement>(null);
	const [oneKeyTime, setOneKeyTime] = useState(0);
	const [open, setOpen] = useState(false);
	const selected =
		selectedIndex >= 0 ? options[selectedIndex] : { name: '--' };
	const menuOptions = options.map(addKey);
	const handleClose = (e: MouseEvent | TouchEvent) => {
		if (
			anchorRef.current &&
			anchorRef.current.contains(e.target as HTMLElement)
		) {
			return;
		}
		setOpen(false);
	};
	const toggleDropdown = () => {
		setOpen(!open);
	};
	const handleMenuItemClick =
		(index: number) => (event: React.MouseEvent<HTMLLIElement>) => {
			event.stopPropagation();
			const selectedMenuItem = options[index];
			selectedMenuItem.onClick && selectedMenuItem.onClick();
			if (switchSelected) {
				setSelectedIndex(index);
				if (onSelectChanged) {
					onSelectChanged(
						index >= 0 && options[index].name
							? options[index].name
							: '--',
					);
				}
			}
			setOpen(false);
		};

	const getScoreColor = (score: number) => {
		let color = '';
		switch (score) {
			case 0:
				color = riskPalette[0].main; //'#E5E5E5';
				break;
			case 1:
				color = riskPalette[1].main; //'#E5E5E5';
				break;
			case 2:
				color = riskPalette[2].main; //'#FAE8B4';
				break;
			case 3:
				color = riskPalette[3].main; //'#FAE8B4';
				break;
			case 4:
				color = riskPalette[4].main; //'#BBDCE5';
				break;
			case 5:
				color = riskPalette[5].main; //'#BBDCE5';
				break;
			case 6:
				color = riskPalette[6].main; //'#C9D8B4';
				break;
			case 7:
				color = riskPalette[7].main; //'#C9D8B4';
				break;
			case 8:
				color = riskPalette[8].main; //'#E6B3B3';
				break;
			case 9:
				color = riskPalette[9].main; //'#E6B3B3';
				break;
			case 10:
				color = riskPalette[10].main; //'#E7C7F3';
				break;
			default:
				color = riskPalette[0].main; //'#E5E5E5';
		}

		return color;
	};

	return (
		<Wrapper ref={anchorRef}>
			{/* @ts-comment TODO - align Variant with our color palette */}
			<RoundButton
				active={true}
				background={getScoreColor(selectedIndex)}
				borderColor={palette.error.main}
				borderSize={selectedIndex >= 0 ? 0 : 1}
				onClick={toggleDropdown}
				onKeyPress={event => {
					let key = Number(event.key);
					if (!isNaN(key)) {
						// Handle the user typing 10 within 1 second
						if (key === 0 && event.timeStamp - oneKeyTime <= 1000) {
							key = 10;
						} else if (key === 1) {
							setOneKeyTime(event.timeStamp);
						}

						setSelectedIndex(key);
						onSelectChanged?.(key.toString());
						setOpen(false);
					}
				}}>
				{selected.name}
				{/* {arrow && <ExpandMore />} */}
				<Popper
					style={{
						zIndex: 1301,
					}}
					open={open}
					anchorEl={anchorRef.current}
					placement={placement}
					transition>
					{({ TransitionProps }) => (
						<Grow {...TransitionProps}>
							<Paper
								square
								style={{ maxHeight: 200, overflow: 'auto' }}>
								<ClickAwayListener onClickAway={handleClose}>
									<MenuList>
										{menuOptions.map(
											({ name, cy, key }, i) => (
												<StyledMenuItem
													// eslint-disable-next-line jsx-a11y/no-autofocus
													autoFocus={
														i === selectedIndex
													}
													key={`menu-item-${name}`}
													selected={
														i === selectedIndex
													}
													onClick={handleMenuItemClick(
														key as number,
													)}
													data-cy={cy}>
													{name}
												</StyledMenuItem>
											),
										)}
									</MenuList>
								</ClickAwayListener>
							</Paper>
						</Grow>
					)}
				</Popper>
			</RoundButton>
		</Wrapper>
	);
};

ScoreButton.defaultProps = {
	variant: 'outlined',
	arrow: true,
	switchSelected: true,
	color: 'primary',
	placement: 'bottom-start',
};

export default ScoreButton;
